/**
 * The short pieces (04 §5), all original: the opening jingle, the high-score loop and the
 * game-over sting.
 */

import { MARCH, TACET } from './grooves';
import type { Song } from './types';

/**
 * A game begins (PLAYER 1 is on screen): two bars in C, 160 BPM, exactly 3 seconds, played once.
 * Three voices and nothing else, as the cabinet had: the lead rockets up the tonic triad and
 * steps down over F, then climbs G7 and walks 3 - 2 - 1 home to a held C, the second voice in
 * sixths below it all the way and the bass landing on the low tonic: a full cadence.
 */
export const START: Song = {
  bpm: 160,
  loop: false,
  bassFloor: 36,
  arpFloor: 60,
  mix: { lead: 0.24, alto: 0.1, arp: 0.1, bass: 0.27, drum: 0.24 },
  order: ['call'],
  patterns: {
    call: {
      chords: 'C F | G7 C',
      lead: `
        E5 G5 C6 E6 . . C6 . F6 . . E6 D6 . C6 . |
        B5 . D6 . G6 . F6 . E6 . D6 . C6 . . .`,
      alto: `
        C5 E5 G5 C6 . . G5 . A5 . . G5 F5 . A5 . |
        G5 . B5 . D6 . B5 . G5 . F5 . E5 . . .`,
      bass: `
        C3 . G3 . C4 . G3 . F3 . A3 . C4 . A3 . |
        G3 . B3 . D4 . G3 . C4 . G3 . C3 . . .`,
      arp: TACET,
    },
  },
};

/**
 * High scores: a triumphant 8-bar loop in C, 144 BPM (13.3 s). A dotted fanfare figure - two
 * hammered notes and a leap up the chord - handed down C, G, A minor and E minor, then up again
 * through F and C to a G chord that climbs to its top note and tumbles back into the loop. The
 * second voice plays the chord tone under every note; the bass marches, rolling its snare into
 * each bar.
 */
export const HISCORE: Song = {
  bpm: 144,
  loop: true,
  bassFloor: 36,
  arpFloor: 60,
  mix: { lead: 0.24, alto: 0.08, arp: 0.1, bass: 0.25, drum: 0.28 },
  order: ['fanfare'],
  patterns: {
    fanfare: {
      chords: 'C | G | Am | Em | F | C | Dm | G',
      lead: `
        C5 . . C5 E5 . G5 . C6 . . . G5 . . . |
        B5 . . B5 D6 . B5 . G5 . . . D5 . . . |
        C6 . . C6 E6 . C6 . A5 . . . E5 . . . |
        B5 . . . G5 . . . E5 . G5 . B5 . . . |
        A5 . . A5 C6 . F6 . . . C6 . A5 . . . |
        G5 . . G5 C6 . E6 . . . C6 . G5 . . . |
        F5 . A5 . D6 . F6 . . . D6 . A5 . . . |
        G5 . B5 . D6 . G6 . . . . . D6 B5 G5 .`,
      alto: `
        G4 . . G4 C5 . E5 . G5 . . . E5 . . . |
        G5 . . G5 B5 . G5 . D5 . . . B4 . . . |
        A5 . . A5 C6 . A5 . E5 . . . C5 . . . |
        G5 . . . E5 . . . B4 . E5 . G5 . . . |
        F5 . . F5 A5 . C6 . . . A5 . F5 . . . |
        E5 . . E5 G5 . C6 . . . G5 . E5 . . . |
        D5 . F5 . A5 . D6 . . . A5 . F5 . . . |
        D5 . G5 . B5 . D6 . . . . . B5 G5 D5 .`,
      bass: { groove: MARCH },
      arp: TACET,
    },
  },
};

/**
 * Game over: four bars falling by step from G5 to a low C through C minor, A-flat, F minor and
 * G, 120 BPM, exactly 8 seconds, played once. The chords are broken slowly under long notes.
 */
export const GAMEOVER: Song = {
  bpm: 120,
  loop: false,
  bassFloor: 36,
  arpFloor: 55,
  mix: { lead: 0.24, alto: 0.07, arp: 0.09, bass: 0.26, drum: 0.22 },
  order: ['fall'],
  patterns: {
    fall: {
      chords: 'Cm | Ab | Fm G | Cm',
      lead: `
        G5 . . . . . F5 . Eb5 . . . D5 . C5 . |
        Eb5 . . . . . D5 . C5 . . . Bb4 . Ab4 . |
        C5 . . . Ab4 . F4 . D5 . . . B4 . G4 . |
        C5 . . . . . . . G4 . . . C4 . . -`,
      alto: `
        Eb5 . . . . . . . C5 . . . . . . . |
        C5 . . . . . . . Ab4 . . . . . . . |
        Ab4 . . . . . . . G4 . . . . . . . |
        G4 . . . . . . . Eb4 . . . G3 . . -`,
      bass: `
        C3 . . . . . . . G2 . . . . . . . |
        Ab2 . . . . . . . Ab2 . . . . . . . |
        F2 . . . . . . . G2 . . . . . . . |
        C2 . . . . . . . . . . . . . . -`,
    },
  },
};
